import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";

import {
    Camera,
    Film,
    Image as ImageIcon,
    Palette,
    Sparkles,
    Scissors,
    Zap,
    Eye,
    User,
    Layers,
    ShoppingBag,
    Heart
} from 'lucide-react';

const fashionServices = [
    { id: '01', icon: Scissors, title: 'High-Fashion Editorials', desc: 'Avant-garde concepts, dramatic narratives, and striking styling designed for magazine spreads.' },
    { id: '02', icon: ShoppingBag, title: 'Seasonal Brand Lookbooks', desc: 'Fluid, lifestyle-driven catalog spreads showcasing designer apparel collections in real-world settings.' },
    { id: '03', icon: Zap, title: 'Avant-Garde Studio Strobe', desc: 'Experimental, high-contrast, and colorful strobe techniques to create boundary-pushing art.' },
    { id: '04', icon: Palette, title: 'E-Commerce Fashion Sets', desc: 'Clean, high-volume apparel shoots curated specifically for major luxury fashion retail platforms.' },
    { id: '05', icon: Film, title: 'Cinematic Fashion Films', desc: 'High-production dynamic vertical video reels and mood pieces optimized for digital campaign drops.' },
    { id: '06', icon: User, title: 'Model Portfolio Builders', desc: 'Curating essential premium looks to showcase an aspiring or elite model’s ultimate structural range.' },
];

const beautyServices = [
    { id: '01', icon: Eye, title: 'Cosmetics & Makeup Campaigns', desc: 'Ultra-crisp macro close-ups highlighting precise product pigments, blending, and textures.' },
    { id: '02', icon: Sparkles, title: 'Skincare & Clean-Beauty Stills', desc: 'Radiant, soft lighting setups capturing flawless dewiness, hydration, and natural complexions.' },
    { id: '03', icon: Layers, title: 'Macro Jewelry & Accessory Sets', desc: 'Pin-sharp focus stacks accentuating the fine micro-facets of luxury jewelry pieces on skin.' },
    { id: '04', icon: Heart, title: 'Haircare & Editorial Styling', desc: 'Dynamic motion capture highlighting split-second bounce, gloss texture, and precise cuts.' },
    { id: '05', icon: Camera, title: 'High-Definition Texturing', desc: 'Flawless close-up portraiture celebrating diverse skin profiles with strict clinical color accuracy.' },
];

const beautyAndFashionDeliverables = [
    { id: '01', icon: Sparkles, title: 'HD Skin Retouching', desc: 'Magazine-grade skin cleanup that preserves natural pore structures and genuine beauty marks.' },
    { id: '02', icon: Scissors, title: 'On-Set Wardrobe Stylists', desc: 'Collaborating with industry stylists to ensure clothing hang, textures, and lines are perfect.' },
    { id: '03', icon: Palette, title: 'HD Makeup Artists (MUA)', desc: 'Elite cosmetic support specialized in flash-safe and ultra-macro studio photography finishes.' },
    { id: '04', icon: Zap, title: 'High-Speed Motion Capture', desc: 'Freezing rapid garment movement, flying hair, and dynamic fabric ripples perfectly.' },
    { id: '05', icon: ImageIcon, title: 'Color-Match Calibration', desc: 'Rigorous display profiling matching fabric and cosmetics colors to exact real-world dyes.' },
    { id: '06', icon: User, title: 'Creative Casting Direction', desc: 'Assisting brands with target-audience alignment when scouting model talent pools.' },
    { id: '07', icon: Film, title: 'BTS Video Sizzle Reels', desc: 'Captivating behind-the-scenes vertical formats designed for maximum viral Instagram and TikTok engagement.' },
    { id: '08', icon: Eye, title: 'Glow & Specular Control', desc: 'Mastering specular reflections on skin oils to emphasize cosmetic highlighters seamlessly.' },
    { id: '09', icon: ImageIcon, title: 'Digital Lookbook Delivery', desc: 'High-speed encrypted digital web galleries for rapid creative agency selection rounds.' },
];

const FashionandBeauty = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.6;
        const speedRight = 0.4;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>

            <Helmet>
                <title>Fashion & Beauty Photography in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Professional Fashion & Beauty Photography in Hyderabad by KhultStudios. We create stunning fashion editorials, beauty campaigns, designer lookbooks, model portfolios, makeup shoots, salon photography, and brand content."
                />

                <meta
                    name="keywords"
                    content="Fashion Photography Hyderabad, Beauty Photography Hyderabad, Editorial Photography, Designer Photography, Fashion Shoot Hyderabad, Makeup Photography, Salon Photography, Cosmetic Photography, Beauty Campaign Photography, Lookbook Photography, Model Photography, Influencer Photography, Clothing Brand Photography, Apparel Photography, Fashion Videography Hyderabad, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/fashion-and-beauty-photography"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/fashion-and-beauty-photography"
                />
                <meta
                    property="og:title"
                    content="Fashion & Beauty Photography in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Premium fashion and beauty photography for designers, brands, models, salons, makeup artists, influencers, and beauty campaigns in Hyderabad."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/fashion-beauty-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Fashion & Beauty Photography | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Creative fashion editorials, beauty campaigns, designer lookbooks, salon photography, and influencer shoots in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/fashion-beauty-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/khultstudio.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Fashion & Beauty Media Production
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Defining Avant-Garde Visual Aesthetics
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        In the industries of fashion and beauty, imagery must challenge convention. At KhultStudio, we frame clothing collections, fine jewelry lines, and global skincare products into striking visual statements that command attention.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Using precision macro lenses, custom-engineered diffusion setups, and high-frequency studio strobes, we create ultra-sharp editorial media ready for luxury lookbooks, cosmetics ads, and digital campaigns.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        View Lookbooks
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=500" alt="High Fashion Editorial Pose" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?q=80&w=500" alt="Macro Cosmetics Makeup Details" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1509631179647-0177331693ae?q=80&w=500" alt="Avant-Garde Studio Fashion" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1512496015851-a90fb38ba796?q=80&w=500" alt="Skincare Dewy Skin CloseUp" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=500" alt="High Fashion Editorial Pose Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?q=80&w=500" alt="Macro Cosmetics Makeup Details Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1509631179647-0177331693ae?q=80&w=500" alt="Avant-Garde Studio Fashion Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1512496015851-a90fb38ba796?q=80&w=500" alt="Skincare Dewy Skin CloseUp Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-and-beauty/fashion-and-beauty-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-and-beauty/fashion-and-beauty-photography2.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-and-beauty/fashion-and-beauty-photography3.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-and-beauty/fashion-and-beauty-photography4.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-and-beauty/fashion-and-beauty-photography5.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-and-beauty/fashion-and-beauty-photography6.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-and-beauty/fashion-and-beauty-photography7.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-and-beauty/fashion-and-beauty-photography8.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* FASHION PHOTOGRAPHY SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">👗 High-End Editorial & Brand Fashion Portfolios</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Fashion is about dynamic energy and identity. From clean, conversion-focused e-commerce layouts to striking avant-garde magazine editorials, our studio frames apparel lines with elegant fluidity and precision styling.
                    </p>
                    <div className="row g-4 mt-2">
                        {fashionServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 1 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BEAUTY PHOTOGRAPHY SERVICES SECTION */}
            <section className="wedding-services-section py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="text-white">✨ Macro Beauty & Cosmetics Advertising Media</h2>
                        <p className="text-light-gray mt-3 max-w-2xl mx-auto">
                            Beauty photography is defined by the fine details. We utilize dedicated macro glass and customized beauty dish systems to render skin texture, product gloss, pigments, and haircare bounce with absolute perfection.
                        </p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {beautyServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FASHION & BEAUTY DELIVERABLES MATRIX */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">💄 Elite Production Deliverables</h2>
                    <div className="row g-4">
                        {beautyAndFashionDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FashionandBeauty
